// Shared helpers for the Books page. Money is integer cents on the wire; the UI shows dollars.

export const usd = (c) => {
  const n = Number(c) || 0;
  const s = (Math.abs(n) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `-$${s}` : `$${s}`;
};

// "1,234.5" or "-20" -> integer cents; null when not a valid amount. String math, no float drift.
export function parseCents(v, { allowNegative = false } = {}) {
  const s = String(v ?? '').replace(/[$,\s]/g, '');
  const m = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m || (m[1] && !allowNegative)) return null;
  const c = Number(m[2]) * 100 + Number((m[3] || '0').padEnd(2, '0'));
  return m[1] ? -c : c;
}
export const centsToInput = (c) => (c == null ? '' : (c / 100).toFixed(2));
export const pct = (bps) => `${(bps / 100).toFixed(bps % 100 ? 2 : 0)}%`;
export const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
export const today = () => new Date().toISOString().slice(0, 10);
export const thisYear = () => new Date().getFullYear();

export const inputCls = 'rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm text-[var(--text-primary)] dark:bg-slate-800';
export const btnCls = 'inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50';
export const btn2Cls = 'inline-flex items-center gap-1 rounded-lg border border-[var(--border)] bg-white px-3 py-1.5 text-sm text-[var(--text-primary)] disabled:opacity-50 dark:bg-slate-800';
export const cardCls = 'rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900';
export const thCls = 'px-3 py-2 text-left text-xs font-medium text-[var(--text-secondary)]';
export const tdCls = 'px-3 py-2 align-top';

const TYPE_LABEL = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity', income: 'Income', cogs: 'Cost of goods sold', expense: 'Expenses', suspense: 'To review' };

export function AccountSelect({ accounts, value, onChange, includeInactive = false, exclude = [], placeholder = 'Choose account', className = '' }) {
  const groups = {};
  for (const a of accounts || []) {
    if ((!a.active && !includeInactive && a.id !== value) || exclude.includes(a.code)) continue;
    (groups[a.type] ||= []).push(a);
  }
  return (
    <select className={`${inputCls} ${className}`} value={value || ''} onChange={(e) => onChange(e.target.value || null)} aria-label="Account">
      <option value="">{placeholder}</option>
      {Object.keys(TYPE_LABEL).filter((t) => groups[t]).map((t) => (
        <optgroup key={t} label={TYPE_LABEL[t]}>
          {groups[t].map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

export function DealSelect({ deals, value, onChange, className = '' }) {
  return (
    <select className={`${inputCls} ${className}`} value={value || ''} onChange={(e) => onChange(e.target.value || null)} aria-label="Deal">
      <option value="">No deal</option>
      {(deals || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
    </select>
  );
}

// Download a CSV export with the signed-in token (a plain link would not carry it).
export async function downloadCsv(path) {
  const res = await fetch(`/api/books${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}` } });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const name = /filename="([^"]+)"/.exec(cd)?.[1] || 'books-export.csv';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function Money({ cents, className = '' }) {
  return <span className={`tabular-nums ${Number(cents) < 0 ? 'text-rose-600 dark:text-rose-400' : ''} ${className}`}>{usd(cents)}</span>;
}

export function Empty({ children }) {
  return <p className="rounded-xl border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--text-secondary)]">{children}</p>;
}
